import { clampBackgroundOpacity, hexToRgba } from './liveTextSettings';

export const QR_MODES = ['fullscreen', 'lower-third'];

export const DEFAULT_QR_MODE_VALUES = {
  call_to_action: 'Ask a question',
  background_color: '#1e3a8a',
  background_image: '',
  text_color: '#ffffff',
  qr_size: 300,
  position_x: 50,
  position_y: 85,
  custom_css: '',
  border_radius: 20,
  shadow_blur: 32,
  shadow_offset_x: 0,
  shadow_offset_y: 8,
  shadow_opacity: 0.3,
  padding: 20,
  background_opacity: 100
};

// Fullscreen historically ignored position/padding/shadow and was flex-centered.
// These defaults keep that look when a session is first split into per-mode settings.
export const DEFAULT_FULLSCREEN_MODE_VALUES = {
  ...DEFAULT_QR_MODE_VALUES,
  position_x: 50,
  position_y: 50,
  padding: 60,
  border_radius: 20,
  shadow_blur: 40,
  shadow_offset_x: 0,
  shadow_offset_y: 10,
  shadow_opacity: 0.3
};

export const DEFAULT_QR_TEMPLATE = {
  ...DEFAULT_QR_MODE_VALUES,
  display_mode: 'fullscreen',
  modes: {
    fullscreen: { ...DEFAULT_FULLSCREEN_MODE_VALUES },
    'lower-third': { ...DEFAULT_QR_MODE_VALUES }
  }
};

export const numberOr = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

export const positiveQrSize = (value, fallback = DEFAULT_QR_MODE_VALUES.qr_size) => {
  const parsed = numberOr(value, fallback);
  return parsed < 1 ? 1 : parsed;
};

export const hasQrCallToAction = (text) => {
  if (text == null) return false;
  return String(text).trim().length > 0;
};

const MODE_KEYS = [
  'call_to_action',
  'background_color',
  'background_image',
  'text_color',
  'qr_size',
  'position_x',
  'position_y',
  'custom_css',
  'border_radius',
  'shadow_blur',
  'shadow_offset_x',
  'shadow_offset_y',
  'shadow_opacity',
  'padding',
  'background_opacity'
];

export const extractLegacyModeValues = (template = {}) => ({
  call_to_action: template.call_to_action !== undefined && template.call_to_action !== null
    ? String(template.call_to_action)
    : DEFAULT_QR_MODE_VALUES.call_to_action,
  background_color: template.background_color || DEFAULT_QR_MODE_VALUES.background_color,
  background_image: template.background_image || '',
  text_color: template.text_color || DEFAULT_QR_MODE_VALUES.text_color,
  qr_size: positiveQrSize(template.qr_size),
  position_x: numberOr(template.position_x, DEFAULT_QR_MODE_VALUES.position_x),
  position_y: numberOr(template.position_y, DEFAULT_QR_MODE_VALUES.position_y),
  custom_css: template.custom_css || '',
  border_radius: numberOr(template.border_radius, DEFAULT_QR_MODE_VALUES.border_radius),
  shadow_blur: numberOr(template.shadow_blur, DEFAULT_QR_MODE_VALUES.shadow_blur),
  shadow_offset_x: numberOr(template.shadow_offset_x, DEFAULT_QR_MODE_VALUES.shadow_offset_x),
  shadow_offset_y: numberOr(template.shadow_offset_y, DEFAULT_QR_MODE_VALUES.shadow_offset_y),
  shadow_opacity: numberOr(template.shadow_opacity, DEFAULT_QR_MODE_VALUES.shadow_opacity),
  padding: numberOr(template.padding, DEFAULT_QR_MODE_VALUES.padding),
  background_opacity: clampBackgroundOpacity(
    template.background_opacity !== undefined && template.background_opacity !== null && template.background_opacity !== ''
      ? template.background_opacity
      : DEFAULT_QR_MODE_VALUES.background_opacity,
    DEFAULT_QR_MODE_VALUES.background_opacity
  )
});

export const normalizeQrModeValues = (values = {}, fallback = DEFAULT_QR_MODE_VALUES) => {
  const source = { ...fallback, ...values };
  return {
    call_to_action: values.call_to_action !== undefined && values.call_to_action !== null
      ? String(values.call_to_action)
      : fallback.call_to_action,
    background_color: source.background_color || fallback.background_color,
    background_image: source.background_image || '',
    text_color: source.text_color || fallback.text_color,
    qr_size: positiveQrSize(source.qr_size, fallback.qr_size),
    position_x: numberOr(source.position_x, fallback.position_x),
    position_y: numberOr(source.position_y, fallback.position_y),
    custom_css: source.custom_css || '',
    border_radius: numberOr(source.border_radius, fallback.border_radius),
    shadow_blur: numberOr(source.shadow_blur, fallback.shadow_blur),
    shadow_offset_x: numberOr(source.shadow_offset_x, fallback.shadow_offset_x),
    shadow_offset_y: numberOr(source.shadow_offset_y, fallback.shadow_offset_y),
    shadow_opacity: numberOr(source.shadow_opacity, fallback.shadow_opacity),
    padding: numberOr(source.padding, fallback.padding),
    background_opacity: clampBackgroundOpacity(
      source.background_opacity !== undefined && source.background_opacity !== null && source.background_opacity !== ''
        ? source.background_opacity
        : fallback.background_opacity,
      fallback.background_opacity
    )
  };
};

const parseStoredModes = (stored) => {
  if (!stored) return null;
  if (typeof stored === 'object') return stored;
  try {
    return JSON.parse(stored);
  } catch (err) {
    return null;
  }
};

export const seedModesFromLegacy = (template = {}) => {
  const shared = extractLegacyModeValues(template);
  return {
    fullscreen: normalizeQrModeValues({
      ...shared,
      position_x: DEFAULT_FULLSCREEN_MODE_VALUES.position_x,
      position_y: DEFAULT_FULLSCREEN_MODE_VALUES.position_y,
      padding: DEFAULT_FULLSCREEN_MODE_VALUES.padding,
      border_radius: DEFAULT_FULLSCREEN_MODE_VALUES.border_radius,
      shadow_blur: DEFAULT_FULLSCREEN_MODE_VALUES.shadow_blur,
      shadow_offset_x: DEFAULT_FULLSCREEN_MODE_VALUES.shadow_offset_x,
      shadow_offset_y: DEFAULT_FULLSCREEN_MODE_VALUES.shadow_offset_y,
      shadow_opacity: DEFAULT_FULLSCREEN_MODE_VALUES.shadow_opacity,
      background_opacity: DEFAULT_FULLSCREEN_MODE_VALUES.background_opacity
    }, DEFAULT_FULLSCREEN_MODE_VALUES),
    'lower-third': normalizeQrModeValues(shared, DEFAULT_QR_MODE_VALUES)
  };
};

export const normalizeQrModes = (template = {}) => {
  const stored = parseStoredModes(template.modes || template.mode_settings);
  if (stored && typeof stored === 'object' && (stored.fullscreen || stored['lower-third'])) {
    const legacy = extractLegacyModeValues(template);
    return {
      fullscreen: normalizeQrModeValues(stored.fullscreen || legacy, DEFAULT_FULLSCREEN_MODE_VALUES),
      'lower-third': normalizeQrModeValues(stored['lower-third'] || legacy, DEFAULT_QR_MODE_VALUES)
    };
  }
  return seedModesFromLegacy(template);
};

export const resolveDisplayMode = (template = {}) => (
  template.display_mode === 'lower-third' ? 'lower-third' : 'fullscreen'
);

export const getActiveQrSettings = (template = {}) => {
  const modes = normalizeQrModes(template);
  return modes[resolveDisplayMode(template)];
};

export const flattenActiveQrSettings = (displayMode, modes) => {
  const mode = displayMode === 'lower-third' ? 'lower-third' : 'fullscreen';
  const active = modes[mode] || DEFAULT_QR_MODE_VALUES;
  const flat = {};
  MODE_KEYS.forEach((key) => {
    flat[key] = active[key];
  });
  return flat;
};

export const setQrModeValues = (template = {}, mode, values) => {
  const normalized = normalizeQrTemplate(template);
  const nextMode = mode === 'lower-third' ? 'lower-third' : 'fullscreen';
  const fallback = nextMode === 'fullscreen' ? DEFAULT_FULLSCREEN_MODE_VALUES : DEFAULT_QR_MODE_VALUES;
  return {
    ...normalized,
    modes: {
      ...normalized.modes,
      [nextMode]: normalizeQrModeValues({ ...normalized.modes[nextMode], ...values }, fallback)
    }
  };
};

export const normalizeQrTemplate = (template = {}) => {
  const display_mode = resolveDisplayMode(template);
  const modes = normalizeQrModes(template);
  const active = modes[display_mode];
  return {
    ...template,
    display_mode,
    modes,
    ...active
  };
};

export const qrBackgroundColor = (settings) => {
  const normalized = normalizeQrModeValues(settings);
  return hexToRgba(normalized.background_color, normalized.background_opacity);
};
